import api from '../axios_helper';
import { Capacitor } from '@capacitor/core';

const PUSH_ENDPOINT = '/push';
const NATIVE_PUSH_TOKEN_STORAGE_KEY = 'gm:native-push-token';

const isAndroidNativeApp = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

const nativePermissionName = (permission) => {
  if (permission === 'granted') return 'granted';
  if (permission === 'denied') return 'denied';
  return 'default';
};

async function getNativePushPlugin() {
  return import('@capacitor/push-notifications');
}

async function createNativeNotificationChannel(PushNotifications) {
  try {
    await PushNotifications.createChannel({
      id: 'gm_notifications',
      name: 'Globe Memories',
      description: 'Comentários, gostos, seguidores e novidades',
      importance: 4,
      vibration: true,
    });
  } catch (_) {
    // Android versions before notification channels do not need this step.
  }
}

async function getNativePushStatus() {
  let permission = 'default';
  let configured = false;
  let enabled = false;
  try {
    const { PushNotifications } = await getNativePushPlugin();
    const permissions = await PushNotifications.checkPermissions();
    permission = nativePermissionName(permissions?.receive);
    const { data } = await api.get(`${PUSH_ENDPOINT}/native/status`);
    configured = Boolean(data?.enabled);
    enabled = configured && permission === 'granted' && Boolean(data?.registered);
  } catch (_) {
    // Push is optional and must not block settings/feed rendering.
  }
  return { supported: true, configured, permission, enabled, subscription: null, native: true };
}

async function subscribeToNativePush() {
  const { PushNotifications } = await getNativePushPlugin();
  const { data: backendStatus } = await api.get(`${PUSH_ENDPOINT}/native/status`);
  if (!backendStatus?.enabled) {
    throw new Error('As notificações nativas ainda não estão configuradas no servidor.');
  }

  let permission = await PushNotifications.checkPermissions();
  if (permission?.receive === 'prompt' || permission?.receive === 'prompt-with-rationale') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission?.receive !== 'granted') {
    throw new Error('Ativa as notificações nas definições do Android para receber alertas.');
  }

  await createNativeNotificationChannel(PushNotifications);
  let registrationListener;
  let errorListener;
  let timeoutId;
  let settled = false;
  let resolveRegistration;
  let rejectRegistration;
  const registrationResult = new Promise((resolve, reject) => {
    resolveRegistration = resolve;
    rejectRegistration = reject;
  });
  const finish = (callback, value) => {
    if (settled) return;
    settled = true;
    callback(value);
  };

  try {
    registrationListener = await PushNotifications.addListener('registration', async ({ value }) => {
      const token = typeof value === 'string' ? value.trim() : '';
      if (!token) {
        finish(rejectRegistration, new Error('O Android não devolveu um token de notificações.'));
        return;
      }
      try {
        await api.post(`${PUSH_ENDPOINT}/native/subscriptions`, { token });
        try { localStorage.setItem(NATIVE_PUSH_TOKEN_STORAGE_KEY, token); } catch (_) { /* storage unavailable */ }
        finish(resolveRegistration, token);
      } catch (error) {
        finish(rejectRegistration, error);
      }
    });
    errorListener = await PushNotifications.addListener('registrationError', (event) => {
      finish(rejectRegistration, new Error(event?.error || 'Não foi possível registar este dispositivo no Firebase.'));
    });
    timeoutId = window.setTimeout(
      () => finish(rejectRegistration, new Error('O registo das notificações demorou demasiado tempo. Tenta novamente.')),
      20000,
    );
    await PushNotifications.register();
    return await registrationResult;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    registrationListener?.remove();
    errorListener?.remove();
  }
}

async function unsubscribeFromNativePush() {
  const { PushNotifications } = await getNativePushPlugin();
  let token = '';
  try { token = localStorage.getItem(NATIVE_PUSH_TOKEN_STORAGE_KEY) || ''; } catch (_) { /* storage unavailable */ }
  if (token) {
    await api.delete(`${PUSH_ENDPOINT}/native/subscriptions`, { data: { token } });
    try { localStorage.removeItem(NATIVE_PUSH_TOKEN_STORAGE_KEY); } catch (_) { /* storage unavailable */ }
  }
  await PushNotifications.unregister();
}

/** Remove this Android device's association with the current user before logout. */
export async function unlinkNativePushTokenFromAccount() {
  if (!isAndroidNativeApp()) return false;
  let token = '';
  try { token = localStorage.getItem(NATIVE_PUSH_TOKEN_STORAGE_KEY) || ''; } catch (_) { /* storage unavailable */ }
  if (!token) return false;
  try {
    await api.delete(`${PUSH_ENDPOINT}/native/subscriptions`, { data: { token } });
    try { localStorage.removeItem(NATIVE_PUSH_TOKEN_STORAGE_KEY); } catch (_) { /* storage unavailable */ }
    return true;
  } catch (error) {
    // Do not prevent logout if the API is temporarily unreachable.
    console.warn('[Globe Memories] Could not unlink the Android push token before logout', error);
    return false;
  }
}

export function isPushSupported() {
  if (isAndroidNativeApp()) return true;
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function getPushStatus() {
  if (isAndroidNativeApp()) return getNativePushStatus();

  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  if (!isPushSupported()) {
    return { supported: false, configured: false, permission, enabled: false, subscription: null };
  }

  try {
    const { data: pushConfig } = await api.get(`${PUSH_ENDPOINT}/public-key`);
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return {
      supported: true,
      configured: Boolean(pushConfig?.enabled && pushConfig?.publicKey),
      permission,
      enabled: Boolean(subscription),
      subscription,
    };
  } catch (_) {
    return { supported: false, configured: false, permission, enabled: false, subscription: null };
  }
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export async function subscribeToPush() {
  if (isAndroidNativeApp()) return subscribeToNativePush();

  if (!isPushSupported()) {
    throw new Error('Este navegador não suporta notificações push.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'As notificações foram bloqueadas no navegador. Ativa-as nas definições do site.'
      : 'A autorização das notificações foi cancelada.');
  }

  const { data } = await api.get(`${PUSH_ENDPOINT}/public-key`);
  if (!data?.enabled || !data.publicKey) {
    throw new Error('As notificações push ainda não estão disponíveis.');
  }

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('O navegador devolveu uma subscrição push inválida.');
  }

  await api.post(`${PUSH_ENDPOINT}/subscriptions`, {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  });

  return subscription;
}

export async function unsubscribeFromPush() {
  if (isAndroidNativeApp()) return unsubscribeFromNativePush();

  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const json = subscription.toJSON();
  await api.delete(`${PUSH_ENDPOINT}/subscriptions`, {
    data: { endpoint: json.endpoint, keys: { p256dh: 'unused', auth: 'unused' } },
  });
  await subscription.unsubscribe();
}
